import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { MailOutboxItem } from "@/lib/notification.api";
import { MAIL_OUTBOX_STATUS_LABELS } from "../mailOutboxPage.constants";
import { formatMailDateTime, humanizeTemplateKey } from "../mailOutboxPage.helpers";

interface MailOutboxDetailDialogProps {
  item: MailOutboxItem | null;
  onOpenChange: (open: boolean) => void;
}

export function MailOutboxDetailDialog({ item, onOpenChange }: MailOutboxDetailDialogProps) {
  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-lg">
        {item && (
          <>
            <DialogHeader>
              <DialogTitle className="text-[17px] font-bold text-foreground">
                {item.subject}
              </DialogTitle>
              <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                {formatMailDateTime(item.createdAt)}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-2">
              <DetailStat label="Destinataire" value={item.recipient} />
              <DetailStat label="Statut" value={MAIL_OUTBOX_STATUS_LABELS[item.status] ?? item.status} />
              <DetailStat label="Modèle" value={humanizeTemplateKey(item.templateKey)} />
              <DetailStat label="Tentatives" value={String(item.retryCount)} />
              {item.sentAt && <DetailStat label="Envoyé le" value={formatMailDateTime(item.sentAt)} />}
              {item.messageId && <DetailStat label="Message ID" value={item.messageId} />}
            </div>

            {item.errorMessage && (
              <div className="rounded-lg border border-danger-border bg-danger-bg px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-danger-text">
                  Erreur
                </p>
                <p className="mt-1 text-[12.5px] text-danger-text">{item.errorMessage}</p>
              </div>
            )}

            {item.metadata && Object.keys(item.metadata).length > 0 && (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-left">
                  <tbody>
                    {Object.entries(item.metadata).map(([key, value]) => (
                      <tr key={key} className="border-b border-border last:border-0">
                        <td className="w-36 bg-surface-muted px-4 py-2 text-[11px] font-medium text-muted-foreground">
                          {key}
                        </td>
                        <td className="break-all px-4 py-2 text-[12px] text-body">
                          {String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-muted px-3 py-2">
      <p className="text-[10.5px] font-medium uppercase tracking-wide text-subtle">{label}</p>
      <p className="mt-0.5 text-[12.5px] text-body">{value}</p>
    </div>
  );
}
